import { computeVisionGraphLabels } from 'affiny-core/helpers/compatibilityReport';
import { computePosition, MATRIX_CHART_SIZE } from 'affiny-core/helpers/graphs';
import {
  useDispatchGetCompatibilityReport,
  useSelectCompatibilityReportGraphs,
} from 'affiny-core/hooks';
import { IGraph } from 'affiny-core/modules/api/models/compatibilityReport/compatibilityReport.types';
import { ECompatibilityReportType } from 'affiny-core/modules/api/models/member/member.types';
import {
  EVisionSectionTitles,
  ICompatibilityReportTabProps,
} from 'affiny-core/modules/compatibilityReport/types';
import {
  Box,
  Flex,
  MatrixGrid,
  rem,
  ShadowBox,
  Text,
  Title,
  ValueBar,
} from 'affiny-ui/src';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CompatibilityReportChapterNavigation } from './CompatibilityReportChapterNavigation';
import { CompatibilityReportIntro } from './CompatibilityReportIntro';

interface ICompatibilityReportVisionChapter
  extends ICompatibilityReportTabProps {
  reportType: ECompatibilityReportType;
  setSection: (arg: string) => void;
}

export const CompatibilityReportVisionChapter = ({
  nickname,
  score,
  reportType,
  wantedGender,
  setSection,
  accountId,
}: ICompatibilityReportVisionChapter) => {
  useDispatchGetCompatibilityReport(accountId, ECompatibilityReportType.VISION);

  const { t } = useTranslation();

  const similarGraphs = useSelectCompatibilityReportGraphs(
    ECompatibilityReportType.VISION,
    accountId,
    'similar'
  );
  const differentGraphs = useSelectCompatibilityReportGraphs(
    ECompatibilityReportType.VISION,
    accountId,
    'different'
  );

  const horizontalGraphs = useSelectCompatibilityReportGraphs(
    ECompatibilityReportType.VISION,
    accountId,
    'horizontal'
  );

  const { SIMILAR, DIFFERENT, FAMILY_RELIGION } = EVisionSectionTitles;

  const matrixValue = (value?: number) =>
    computePosition(MATRIX_CHART_SIZE, value);

  const renderMatrixGraph = (graphs: IGraph[], titleKey: string) => (
    <Box mt="xLarge">
      <Title mb="xLarge">{t(`compatibilityReport.vision.${titleKey}`)}</Title>
      {graphs.map((item, index) => {
        const matrix = item.matrices[0];
        const translationKeys = computeVisionGraphLabels(
          matrix.translationKeys
        );

        return (
          <Flex key={item.id} alignItems="flex-end" mb="xLarge">
            <Box>
              <ShadowBox
                p="large"
                pt="medium"
                mr="xLarge"
                width={rem(350)}
                display="flex"
                flexDirection="column"
                alignItems="center"
              >
                <Text mb="medium">
                  {t(`compatibilityReport.vision.graphLabels.${item.id}`)}
                </Text>
                <MatrixGrid
                  titleTopLeft={t(translationKeys.titleTopLeft)}
                  titleTopRight={t(translationKeys.titleTopRight)}
                  titleBottomLeft={t(translationKeys.titleBottomLeft)}
                  titleBottomRight={t(translationKeys.titleBottomRight)}
                  valueX={matrixValue(matrix.viewer.x)}
                  valueY={matrixValue(matrix.viewer.y)}
                  secondaryValueX={matrixValue(matrix.viewee.x)}
                  secondaryValueY={matrixValue(matrix.viewee.y)}
                  textValue={t('compatibilityReport.graphs.me')}
                  textSecondaryValue={t('compatibilityReport.graphs.other', {
                    WANTED_GENDER: wantedGender,
                  })}
                />
              </ShadowBox>
            </Box>
            {item.analysis?.length && nickname && (
              <Box>
                <Text fontWeight="600" fontSize={rem(18)} mb="large">
                  {t(`compatibilityReport.vision.analysis`)}
                </Text>
                {item.analysis.map((analysis, idx) => (
                  <Text key={`vision-${titleKey}-${index}-${idx}`}>
                    {analysis.replace(/\[UL_PSEUDO\]/gm, nickname)}
                  </Text>
                ))}
              </Box>
            )}
          </Flex>
        );
      })}
    </Box>
  );

  const renderValueBarGraph = () => (
    <>
      <Title mb="xLarge">
        {t(`compatibilityReport.vision.${FAMILY_RELIGION}`)}
      </Title>
      {horizontalGraphs.map((item, index) => {
        const matrix = item.matrices[0];

        return (
          <ShadowBox key={item.id} p="large" pt="medium" mb="xLarge">
            <Text mb="large">
              {t(`compatibilityReport.vision.graphLabels.${item.id}`)}
            </Text>
            <Flex justifyContent="space-between">
              <Box width={rem(300)}>
                <ValueBar
                  title={t(
                    `compatibilityReport.vision.graphLabels.${matrix.translationKeys[0]}`
                  )}
                  rightTitle={t(
                    `compatibilityReport.vision.graphLabels.${matrix.translationKeys[1]}`
                  )}
                  value={matrix.viewee.x || 0}
                  secondaryValue={matrix.viewer.x || 0}
                  text={t('compatibilityReport.graphs.other', {
                    WANTED_GENDER: wantedGender,
                  })}
                  secondaryText={t('compatibilityReport.graphs.me')}
                  color="accent"
                  titleSize={10}
                  titleColor="secondary"
                />
              </Box>

              <Box width={rem(500)} alignItems="flex-end">
                {item.analysis?.length &&
                  nickname &&
                  item.analysis.map((analysis, idx) => (
                    <Text key={`vision-horizontalgraphs-${index}-${idx}`}>
                      {analysis.replace(/\[UL_PSEUDO\]/gm, nickname)}
                    </Text>
                  ))}
              </Box>
            </Flex>
          </ShadowBox>
        );
      })}
    </>
  );

  return (
    <Box>
      {score && nickname && (
        <CompatibilityReportIntro
          nickname={nickname}
          score={score}
          reportType={reportType}
        />
      )}

      {similarGraphs.length > 0 && renderMatrixGraph(similarGraphs, SIMILAR)}
      {differentGraphs.length > 0 &&
        renderMatrixGraph(differentGraphs, DIFFERENT)}
      {horizontalGraphs.length > 0 && renderValueBarGraph()}

      <CompatibilityReportChapterNavigation
        goToNextChapter={() => setSection('personalities')}
        textNextChapter={t('compatibilityReport.tabs.personalities')}
        goToPreviousChapter={() => setSection('values')}
        textPreviousChapter={t('compatibilityReport.tabs.values')}
      />
    </Box>
  );
};
